/**
 * Generic hook to restore scroll position after widgets are restored
 * Waits for widgets to render before restoring scroll position
 * Generic for all PL pages (not page-specific)
 * Global and key-based - works with any cache manager and key generator
 */

import { useEffect, useRef } from 'react'

import { usePLPageMeta } from '@src/components/pl-page-context'
import { getCollectionCacheKey } from '@src/pages/[...slug]/components/collection/utils'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { defaultScrollCacheAdapter } from '../utils/scroll-cache-adapters'

import type { CacheKeyGenerator, ScrollCacheManager } from '../contracts/scroll-cache'

export interface UsePLScrollRestorationOptions {
  /**
   * Cache key generator function
   * Defaults to getCollectionCacheKey for backward compatibility
   */
  cacheKeyGenerator?: CacheKeyGenerator
  /**
   * Scroll cache manager
   * Defaults to collection cache adapter for backward compatibility
   */
  cacheManager?: ScrollCacheManager
}

/**
 * Generic hook to restore scroll position after widgets are restored and rendered
 * Should be used in PL page components within PLPageProvider
 * Uses page metadata from PLPageProvider context
 *
 * @param options - Optional configuration for cache key generation and cache manager
 * @example
 * // Default (collection pages)
 * usePLScrollRestoration()
 *
 * // Search pages
 * usePLScrollRestoration({
 *   cacheKeyGenerator: getSearchCacheKey,
 *   cacheManager: searchScrollCacheAdapter
 * })
 */
export function usePLScrollRestoration(options?: UsePLScrollRestorationOptions) {
  const pageMeta = usePLPageMeta()
  const hasRestoredRef = useRef(false)

  // Use provided options, pageMeta config, or defaults (priority: options > pageMeta > defaults)
  const cacheKeyGenerator = options?.cacheKeyGenerator ?? pageMeta.scrollCacheKeyGenerator ?? getCollectionCacheKey
  const cacheManager = options?.cacheManager ?? pageMeta.scrollCacheManager ?? defaultScrollCacheAdapter

  // Generate cache key using provided generator
  const cacheKey = cacheKeyGenerator(pageMeta.pathname, pageMeta.searchParams)

  // Check if scroll restoration is enabled for this page. Under DS the anchor-based engine
  // (`@src/scroll-restoration/use-back-nav-scroll-restoration`) owns placement on these pages, so this
  // pixel restore stands down — two restorers moving the viewport would fight. The save side is
  // untouched: the cache's `scrollY` is still written for legacy traffic and for a DS→legacy flip.
  // Read at render time; the DS decision is per-page and can flip between client navigations.
  // [STRFRNT-16138]
  const isEnabled = pageMeta.enableScrollRestoration !== false && !shouldUseDesignSystem()

  useEffect(() => {
    // Skip if disabled or already restored
    if (!isEnabled || hasRestoredRef.current) return

    try {
      // Get cached scroll position using provided cache manager
      const scrollY = cacheManager.getScrollPosition(cacheKey)

      if (scrollY && scrollY > 0) {
        // Use MutationObserver to wait for content to be rendered
        const observer = new MutationObserver(mutations => {
          try {
            const hasContentChanges = mutations.some(
              mutation => mutation.type === 'childList' && mutation.addedNodes.length > 0,
            )

            if (hasContentChanges) {
              // Small delay to ensure layout is complete
              setTimeout(() => {
                window.scrollTo(0, scrollY)
                hasRestoredRef.current = true
                observer.disconnect()
              }, 10)
            }
          } catch (error) {
            console.error('Error in scroll restoration observer:', error)
            observer.disconnect()
          }
        })

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        })

        // Fallback: restore after a timeout if no mutations occur
        const fallbackTimeout = setTimeout(() => {
          window.scrollTo(0, scrollY)
          hasRestoredRef.current = true
          observer.disconnect()
        }, 500)

        return () => {
          observer.disconnect()
          clearTimeout(fallbackTimeout)
        }
      }
      hasRestoredRef.current = true
    } catch (error) {
      console.error('Error restoring scroll position:', error)
      hasRestoredRef.current = true
    }
    return undefined
  }, [cacheKey, cacheManager, isEnabled])
}
